import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ApiTokenInfo {
  hasToken: boolean;
  status: 'active' | 'inactive' | 'revoked';
  createdAt?: string;
  lastUsedAt?: string;
  token?: string;
}

interface ApiTokenResponse {
  success: boolean;
  message?: string;
  token?: string;
  error?: string;
}

// Hook to get API token information
export const useApiToken = (organizationId: string) => {
  return useQuery({
    queryKey: ['api-token', organizationId],
    queryFn: async (): Promise<ApiTokenInfo> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await supabase.functions.invoke('manage-api-token', {
        body: {
          action: 'get',
          organizationId,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return response.data;
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

// Hook to generate a new API token
export const useGenerateApiToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId }: { organizationId: string }): Promise<ApiTokenResponse> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await supabase.functions.invoke('manage-api-token', {
        body: {
          action: 'generate',
          organizationId,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return response.data;
    },
    onSuccess: (_, { organizationId }) => {
      // Invalidate and refetch the API token info
      queryClient.invalidateQueries({ queryKey: ['api-token', organizationId] });
    },
  });
};

// Hook to revoke an API token
export const useRevokeApiToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId }: { organizationId: string }): Promise<ApiTokenResponse> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await supabase.functions.invoke('manage-api-token', {
        body: {
          action: 'revoke',
          organizationId,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return response.data;
    },
    onSuccess: (_, { organizationId }) => {
      // Invalidate and refetch the API token info
      queryClient.invalidateQueries({ queryKey: ['api-token', organizationId] });
    },
  });
};